import { useState } from 'react';

import type { Booking, PrintLetterhead } from '@/api/bookings';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { useOpenAtTop } from '@/components/useOpenAtTop';
import { booked, receiptPayments } from '@core/bookings/booked';
import { referenceLabel } from '@core/bookings/reference';
import { receiptSheet } from '@core/print/sheetHtml';

import { SheetPrint } from '../print/SheetPrint';
import { MoneyLine, MoneyLines } from './MoneyLine';

/**
 * The moment after Confirm — the booking redesign's "Booked" screen. The number to quote, what was
 * paid just now and what is owed at pickup, then the customer copy to hand over, with the booking
 * and the next customer one click away.
 *
 * <p>
 * <strong>It replaced dropping straight into the booking's detail</strong>, which opened on a stage
 * tracker and a party table — the screen for the next visit, not this one — and said nothing when
 * the token failed to record. Every word is the core's `booked`, which the phone's screen reads too.
 * </p>
 *
 * <p>
 * It writes nothing, so the insert/update rule has nothing to say about it; it is a page because it
 * replaces the booking page it follows.
 * </p>
 */
export function BookedPage({
  booking,
  tokenFailed,
  letterhead,
  currency,
  onOpen,
  onAnother,
}: {
  booking: Booking;
  tokenFailed: boolean;
  /** Absent until it loads; the customer copy is offered once it is there. */
  letterhead: PrintLetterhead | undefined;
  currency: string;
  onOpen: () => void;
  onAnother: () => void;
}) {
  const [printing, setPrinting] = useState(false);
  // Fixed when the page opens, so the preview and the print say the same time.
  const [printedAt] = useState(() => new Date().toISOString());
  const words = booked(booking, { tokenFailed, currency });
  // The token's receipt, with the deposit taken in the same press — the phone's Booked screen.
  const receipt = receiptPayments(words, booking);

  useOpenAtTop();

  if (printing && letterhead) {
    return (
      <SheetPrint
        eyebrow={`${referenceLabel(booking)} · ${booking.customerName}`}
        title={receipt ? 'Receipt' : 'Booking summary'}
        remember="receipt"
        build={(paper, rollLength) =>
          receiptSheet(
            {
              booking,
              letterhead,
              payment: receipt?.payment ?? null,
              together: receipt?.together ?? [],
              printedAt,
            },
            paper,
            rollLength,
          )
        }
        onClose={() => setPrinting(false)}
      />
    );
  }

  return (
    <>
      <PageHeading
        eyebrow={`Bookings · ${words.reference}`}
        title={words.title}
        subtitle={words.sentence}
        overlap
      />

      <div className="mx-auto w-full max-w-3xl">
        <Card>
          <div className="flex items-center gap-4">
            <span className="bg-success-transparent flex size-13 shrink-0 items-center justify-center rounded-full text-[color:var(--color-success)]">
              <i className="icon icon-check text-[24px]" />
            </span>
            <div className="min-w-0">
              <p className="text-default mb-0 text-[length:var(--scale-list-meta)] font-semibold uppercase">
                {words.referenceLabel}
              </p>
              <p className="font-mono-nums mb-0 truncate font-serif text-[26px] leading-tight font-semibold text-gray-900">
                {words.reference}
              </p>
            </div>
          </div>

          <dl className="border-border-color mt-5 grid grid-cols-1 gap-x-6 gap-y-3 border-t pt-4 sm:grid-cols-2">
            {words.facts.map((fact) => (
              <div key={fact.label}>
                <dt className="text-default text-[length:var(--scale-list-meta)]">{fact.label}</dt>
                <dd className="mb-0 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                  {fact.value}
                </dd>
              </div>
            ))}
          </dl>

          <div className="bg-light mt-5 rounded-xl p-4">
            <p className="mb-2 text-sm font-semibold text-gray-900">The money</p>
            <MoneyLines>
              {words.money.map((line) => (
                <MoneyLine
                  key={line.label}
                  label={line.label}
                  value={line.value}
                  strong={line.strong ?? false}
                  {...(line.strong ? { tone: 'warning' as const } : {})}
                />
              ))}
            </MoneyLines>
          </div>

          {words.tokenProblem && (
            <div className="mt-4">
              <Notice tone="warning" title="The money is not on the booking">
                {words.tokenProblem}
              </Notice>
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            <Button
              variant="primary"
              icon="icon icon-printer"
              shortcut="print"
              disabled={!letterhead}
              onClick={() => setPrinting(true)}
              className="h-11 flex-1 basis-48 justify-center"
            >
              {receipt ? 'Print the receipt' : 'Print the booking summary'}
            </Button>
            <Button
              icon="icon icon-plus"
              shortcut="create"
              onClick={onAnother}
              className="h-11 flex-1 basis-48 justify-center"
            >
              Take another booking
            </Button>
          </div>
          <p className="mt-4 mb-0 text-center">
            <button
              type="button"
              onClick={onOpen}
              className="text-primary cursor-pointer text-[length:var(--scale-list-name)] font-semibold hover:underline"
            >
              Open the booking →
            </button>
          </p>
        </Card>
      </div>
    </>
  );
}
